module.exports = function buildBestBacktestingSoftware({ agentProofPoints }) {
  const SOURCE_VERIFIED_DATE = '2026-08-14';

  return {
    path: '/guides/best-backtesting-software',
    title: 'Best Backtesting Software by Trading Workflow | BotSpot',
    description: 'Compare backtesting software for AI-assisted, no-code, chart-based, code-first, and MQL5 workflows using documented features and limitations.',
    pageType: 'article',
    lastmod: SOURCE_VERIFIED_DATE,
    content: {
      eyebrow: 'Backtesting software buyer guide',
      heading: 'Best backtesting software depends on your strategy workflow',
      summary: 'No single backtester fits every trader. Match six products to the way you author strategies, source data, model execution, inspect results, and move toward paper or live operation. Recommendations are editorial workflow matches based on official documentation, not independent performance rankings.',
      primaryAction: { label: 'Backtest a strategy in BotSpot', url: '/ai-agent' },
      secondaryAction: { label: 'Learn defensible backtesting', url: '/guides/backtesting-ai-trading-strategies' },
      proofPoints: agentProofPoints,
      cards: [
        {
          eyebrow: 'Workflow match: AI agent',
          heading: 'BotSpot',
          summary: 'For conversational strategy creation, inspectable Lumibot code, managed backtests, revisions, artifacts, and supported deployment workflows.',
          url: '/ai-agent',
        },
        {
          eyebrow: 'Workflow match: charts and Pine',
          heading: 'TradingView',
          summary: 'For chart-centered analysis, Pine Script strategies, built-in or community scripts, and visual Strategy Reports.',
          url: '/compare/botspot-vs-tradingview',
        },
        {
          eyebrow: 'Workflow match: no-code technical rules',
          heading: 'TrendSpider',
          summary: 'For natural-language or point-and-click technical strategies, visual testing, result analysis, and alert or bot workflows.',
          url: '/compare/botspot-vs-trendspider',
        },
        {
          eyebrow: 'Workflow match: portfolio rules',
          heading: 'Composer',
          summary: 'For AI-assisted or visual portfolio Symphonies, allocation logic, benchmark comparisons, and integrated automated execution.',
          url: '/compare/botspot-vs-composer',
        },
        {
          eyebrow: 'Workflow match: quantitative code',
          heading: 'QuantConnect',
          summary: 'For Python or C# research, LEAN algorithms, cloud or local testing, detailed result analysis, and quantitative deployment controls.',
          url: '/compare/botspot-vs-quantconnect',
        },
        {
          eyebrow: 'Workflow match: MQL5 robots',
          heading: 'MetaTrader 5',
          summary: 'For MQL5 Expert Advisors, broker-supplied history, tick-model choices, parameter optimization, and forward testing.',
          url: 'https://www.metatrader5.com/en/terminal/help/algotrading/testing',
        },
      ],
      sections: [
        {
          heading: 'How this guide selects and evaluates software',
          paragraphs: [
            'This guide includes six products with distinct documented approaches to strategy authoring, historical simulation, result inspection, and operational follow-through. Inclusion does not mean every product fits every asset, broker, region, frequency, or account.',
            'Workflow recommendations are editorial inferences from official product documentation reviewed on the verification date. BotSpot did not run a standardized independent benchmark of simulation accuracy, data quality, execution speed, or investment returns.',
          ],
          bullets: [
            'No product receives a universal first-place ranking.',
            'Order reflects workflow variety, not performance.',
            'Current subscription prices are omitted because plans and entitlements change.',
            'Product claims remain attributed to official documentation rather than presented as independent test results.',
            'Users should verify current data, asset, broker, plan, and regional availability before choosing.',
          ],
        },
        {
          heading: 'What to compare before choosing a backtester',
          bullets: [
            'Strategy authoring: conversational AI, visual blocks, point-and-click conditions, Pine Script, Python, C#, or MQL5.',
            'Historical data: assets, resolution, corporate actions, symbol history, publication timing, and missing-data behavior.',
            'Execution model: order timing, prices, spreads, fees, slippage, latency, liquidity, partial fills, and market impact.',
            'Validation controls: out-of-sample periods, forward testing, parameter tracking, and reproducible configurations.',
            'Evidence: trades, timestamps, logs, charts, benchmarks, drawdowns, exposure, turnover, and downloadable artifacts.',
            'Operational continuity: paper trading, broker compatibility, monitoring, deployment, and comparison with live behavior.',
          ],
        },
        {
          heading: 'BotSpot: workflow match for AI-assisted strategy iteration',
          paragraphs: [
            'BotSpot adds a managed workflow around Lumibot. Users can describe rules conversationally, inspect generated strategy code, run and revise backtests, review charts, trades, logs, files, and audit history, then use supported broker connections for later paper or live workflows.',
            'This fit is strongest when strategy creation, backtest iteration, evidence review, and supported deployment should stay in one agent-led workspace.',
          ],
          bullets: [
            'Authoring: conversational specification with inspectable Lumibot strategy code.',
            'Testing: managed historical runs using supported hosted data workflows.',
            'Evidence: generated artifacts, charts, trades, logs, files, and revision history.',
            'Watch: generated code and assumptions still require review; data and deployment options depend on current product and broker support.',
          ],
        },
        {
          heading: 'TradingView: workflow match for charts and Pine Script',
          paragraphs: [
            'TradingView strategies are Pine scripts that simulate hypothetical orders across historical and realtime chart bars. Built-in, community, and personal strategies can produce chart markers and a Strategy Report containing metrics and trade detail.',
            'This fit is strongest when charts, indicators, Pine Script, and visual inspection already anchor the research workflow.',
          ],
          bullets: [
            'Authoring: Pine Script plus built-in and published community strategies.',
            'Testing: historical backtesting and realtime forward testing on chart data.',
            'Evidence: report metrics, trades, equity behavior, and chart markers.',
            'Watch: custom work requires Pine knowledge; synthetic prices on nonstandard charts can produce unrealistic results.',
          ],
        },
        {
          heading: 'TrendSpider: workflow match for no-code technical strategies',
          paragraphs: [
            'TrendSpider documents a Strategy Tester that accepts natural-language or point-and-click entry, exit, and risk rules. Optional custom JavaScript indicators can extend the visual workflow, and documented results include trade-level output and risk statistics.',
            'This fit is strongest for technical traders who want charts, conditions, testing, alerts, and bots without making general-purpose strategy code the main interface.',
          ],
          bullets: [
            'Authoring: natural language, point-and-click conditions, or optional JavaScript indicators.',
            'Testing: visual rule testing across documented symbols, timeframes, and historical coverage.',
            'Evidence: trades, performance statistics, drawdown, and other strategy metrics.',
            'Watch: confirm current history depth, run limits, asset coverage, broker support, and plan entitlements for the intended workflow.',
          ],
        },
        {
          heading: 'Composer: workflow match for rules-based portfolio allocation',
          paragraphs: [
            'Composer represents strategies as editable Symphonies built with AI assistance or visual blocks for assets, weights, conditions, filters, and groups. Its product documentation shows benchmark comparisons, historical allocations, fees, slippage, and final-value modeling.',
            'This fit is strongest for investors building rules-based stock and ETF allocation or rebalancing strategies inside Composer’s integrated brokerage workflow.',
          ],
          bullets: [
            'Authoring: AI-assisted creation or a no-code visual Symphony editor.',
            'Testing: portfolio backtests with benchmark and strategy comparisons.',
            'Evidence: performance metrics, historical allocation, modeled fees, and slippage.',
            'Watch: documented stock and ETF backtests use daily adjusted closing prices, which do not represent intraday market quotes.',
          ],
        },
        {
          heading: 'QuantConnect: workflow match for code-first quantitative research',
          paragraphs: [
            'QuantConnect runs LEAN algorithms through documented cloud, local, and command-line workflows. Projects support research, backtests, optimization, and live deployment, while result APIs and notebooks support deeper trade and chart analysis.',
            'This fit is strongest for developers and quantitative teams that want Python or C#, broad research controls, and explicit ownership of algorithm configuration.',
          ],
          bullets: [
            'Authoring: Python or C# against the LEAN engine.',
            'Testing: cloud or local algorithm backtests with configurable data and models.',
            'Validation: documented out-of-sample holdouts and research guidance.',
            'Watch: coding and model configuration carry a learning curve; QuantConnect documents expected differences between backtest and live behavior.',
          ],
        },
        {
          heading: 'MetaTrader 5: workflow match for MQL5 Expert Advisors',
          paragraphs: [
            'MetaTrader 5 includes a Strategy Tester for MQL5 Expert Advisors and custom indicators. Official documentation covers multi-currency tests, parameter optimization, forward-period splits, tick-generation choices, visual tests, and execution-delay emulation.',
            'This fit is strongest when the strategy, broker, historical data, and deployment workflow already center on MetaTrader and MQL5.',
          ],
          bullets: [
            'Authoring: MQL5 Expert Advisors and indicators.',
            'Testing: single runs, multi-parameter optimization, multiple tick modes, and visual testing.',
            'Validation: forward-period splits can separate parameter fitting from later checks.',
            'Watch: data and available symbols depend on the connected trading server; tick mode, delay, commission, margin, and symbol settings materially affect results.',
          ],
        },
        {
          heading: 'Backtesting software cannot prove a strategy will work live',
          paragraphs: [
            'Every product simulates history through data and execution assumptions. Results can fail because of look-ahead bias, overfitting, bad data, omitted costs, unrealistic fills, software defects, or market changes.',
            'Choose software that makes assumptions and trade behavior inspectable. Preserve unseen data, test current operations in paper mode where available, and treat live deployment as a separate risk decision.',
          ],
        },
      ],
      faq: [
        {
          question: 'Which backtesting software is best for beginners?',
          answer: 'No universal beginner choice exists. BotSpot fits users who want an AI agent and inspectable generated code. TrendSpider emphasizes point-and-click technical rules. Composer emphasizes visual portfolio Symphonies. TradingView offers built-in strategies but custom strategies use Pine Script.',
        },
        {
          question: 'Which backtesting software supports Python?',
          answer: 'QuantConnect supports Python algorithms in a code-first quantitative platform. BotSpot generates and operates inspectable Lumibot strategies, and Lumibot is a Python framework. Compare managed workflow needs against direct code and infrastructure control.',
        },
        {
          question: 'Can I compare returns from two different backtesting platforms directly?',
          answer: 'Not safely without reconciling data, dates, corporate actions, order timing, prices, fees, slippage, benchmarks, and other assumptions. Composer explicitly notes that platforms can use different backtest assumptions.',
        },
        {
          question: 'Does backtesting software predict live trading results?',
          answer: 'No. Backtests are historical simulations. Data availability, execution, latency, liquidity, fees, market impact, outages, and future market conditions can produce different live results.',
        },
        {
          question: 'Should I choose a backtester before choosing a broker?',
          answer: 'Evaluate both together. Asset coverage, historical data, paper mode, order types, account permissions, and deployment support can determine whether a strategy can move beyond historical testing.',
        },
      ],
      relatedLinks: [
        { label: 'Backtest AI trading strategies', url: '/guides/backtesting-ai-trading-strategies' },
        { label: 'Understand backtesting metrics', url: '/guides/backtesting-metrics-explained' },
        { label: 'Avoid strategy overfitting', url: '/guides/avoid-overfitting-trading-strategies' },
        { label: 'Choose an automated trading platform', url: '/guides/automated-trading-platforms' },
        { label: 'Compare AI trading platforms', url: '/compare' },
      ],
      sources: [
        { label: 'BotSpot MCP for Agents', url: '/agents' },
        { label: 'BotSpot broker connection manifest', url: '/.well-known/botspot-manifest.json' },
        { label: 'BotSpot Terms of Service', url: '/terms' },
        { label: 'Lumibot backtesting documentation', url: 'https://lumibot.lumiwealth.com/backtesting.html' },
        { label: 'TradingView strategy documentation', url: 'https://www.tradingview.com/pine-script-docs/concepts/strategies/' },
        { label: 'TrendSpider strategy development and backtesting', url: 'https://trendspider.com/product/strategy-development-and-backtesting-tools/' },
        { label: 'Composer product overview', url: 'https://www.composer.trade/' },
        { label: 'Composer backtest basics', url: 'https://help.composer.trade/article/67-backtest-basics' },
        { label: 'QuantConnect backtesting documentation', url: 'https://www.quantconnect.com/docs/v2/cloud-platform/backtesting/getting-started' },
        { label: 'QuantConnect live reconciliation documentation', url: 'https://www.quantconnect.com/docs/v2/writing-algorithms/live-trading/reconciliation' },
        { label: 'MetaTrader 5 Strategy Tester documentation', url: 'https://www.metatrader5.com/en/terminal/help/algotrading/testing' },
      ],
      verifiedAt: SOURCE_VERIFIED_DATE,
    },
  };
};
